import { useState, useEffect, useCallback } from 'react'
import type { Tool } from '../types'
import * as db from '../services/db'

/**
 * Manages a single flat tool inventory per user.
 * Under the hood still uses the `tool_libraries` table (no DB migration needed)
 * but treats all rows as one merged inventory, stored in a single "default" row.
 */
export function useToolInventory() {
  const [tools, setToolsState] = useState<Tool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Track the single inventory row ID
  const [inventoryRowId, setInventoryRowId] = useState<string | null>(null)

  useEffect(() => {
    db.fetchToolLibraries()
      .then(async (libraries) => {
        if (libraries.length === 0) {
          // No inventory yet — start empty
          setToolsState([])
          setInventoryRowId(null)
        } else if (libraries.length === 1) {
          // Single row — use as-is
          setToolsState(libraries[0].tools)
          setInventoryRowId(libraries[0].id)
        } else {
          // Legacy: multiple library rows — merge all tools into one row, delete extras
          const merged: Tool[] = []
          const usedNumbers = new Set<number>()
          for (const lib of libraries) {
            for (const tool of lib.tools) {
              let num = tool.number
              while (usedNumbers.has(num)) num++
              usedNumbers.add(num)
              merged.push({ ...tool, id: tool.id || crypto.randomUUID(), number: num })
            }
          }

          // Keep the first row as the canonical one
          const keepId = libraries[0].id
          await db.updateToolLibrary(keepId, {
            name: 'Inventory',
            description: '',
            tools: merged,
          })
          // Delete extras
          for (let i = 1; i < libraries.length; i++) {
            await db.deleteToolLibrary(libraries[i].id).catch(() => {})
          }

          setToolsState(merged)
          setInventoryRowId(keepId)
        }
      })
      .catch(err => setError(String(err?.message ?? err)))
      .finally(() => setLoading(false))
  }, [])

  const persistTools = useCallback(async (newTools: Tool[]) => {
    if (inventoryRowId) {
      await db.updateToolLibrary(inventoryRowId, { tools: newTools })
    } else {
      // Create the inventory row
      const id = crypto.randomUUID()
      const lib = { id, name: 'Inventory', description: '', tools: newTools }
      await db.insertToolLibrary(lib)
      setInventoryRowId(id)
    }
  }, [inventoryRowId])

  const setTools = useCallback((newTools: Tool[]) => {
    setToolsState(newTools)
    persistTools(newTools).catch(err => setError(String(err?.message ?? err)))
  }, [persistTools])

  const addTool = useCallback((tool: Tool) => {
    setToolsState(prev => {
      const next = [...prev, tool]
      persistTools(next).catch(err => setError(String(err?.message ?? err)))
      return next
    })
  }, [persistTools])

  const removeTool = useCallback((toolId: string) => {
    setToolsState(prev => {
      const next = prev.filter(t => t.id !== toolId)
      persistTools(next).catch(err => setError(String(err?.message ?? err)))
      return next
    })
  }, [persistTools])

  return { tools, setTools, addTool, removeTool, loading, error }
}
